import { MediaSizes } from '../../../../../../../../../../components/MediaApplicationContext';
import { ArrayUtil } from '../../../../../../../../../../utils/array.utils';
import FileUtil from '../../../../../../../../../../utils/file.utils';
import { OfflineImageCacheManager } from '../../../offlineImageCacheManager';
import { ImageToCache } from '../types/offlineImageCacher.util.types';
import { getImageResponseToCache } from './getImageResponseToCache.util';

export class OfflineImageCacher {
  private totalImageCacheSizeInBytes = 0;

  /**
   * @param maxCacheSizeInBytes maximum total bytes to cache before skipping further images.
   * @param chunkSize number of concurrent requests per batch.
   * @param webpEnabled whether to cache images in WebP format when no extension is present.
   */
  constructor(
    private readonly maxCacheSizeInBytes: number,
    private readonly chunkSize = 10,
    private readonly webpEnabled: boolean,
  ) {}

  async cacheImages(
    items: ImageToCache[],
    sizeToProcessMedia: MediaSizes,
  ): Promise<{ successItemsMap: Map<string, string> }> {
    const cacheManager = await OfflineImageCacheManager.create();

    const successItemsMap = new Map<string, string>();

    if (!cacheManager || !items.length) {
      return { successItemsMap };
    }

    const toProcess = this.getItemsToProcess(items, sizeToProcessMedia);

    const chunks = ArrayUtil.chunk(toProcess, this.chunkSize);

    for (const batch of chunks) {
      await Promise.all(
        batch.map(async ({ id, resolvedUrl }) => {
          if (this.cannotSaveImages()) {
            return;
          }

          const responseToCache =
            (await cacheManager.getCache(resolvedUrl)) ||
            (await getImageResponseToCache(resolvedUrl));

          if (!responseToCache) {
            return;
          }

          await cacheManager.cacheResponse(resolvedUrl, responseToCache);
          this.afterSaveImage(responseToCache);
          successItemsMap.set(id, resolvedUrl);
        }),
      );
    }

    return { successItemsMap };
  }

  private getItemsToProcess(items: ImageToCache[], sizeToProcessMedia: MediaSizes) {
    return items.map(({ id, url }) => ({
      id,
      resolvedUrl: this.handleCacheUrlExtension(url, sizeToProcessMedia),
    }));
  }

  private cannotSaveImages(): boolean {
    return this.totalImageCacheSizeInBytes >= this.maxCacheSizeInBytes;
  }

  private afterSaveImage(response: Response): void {
    const contentLength = response.headers.get('Content-Length');
    if (contentLength) {
      this.totalImageCacheSizeInBytes += Number(contentLength) || 0;
    }
  }

  private handleCacheUrlExtension(url: string, sizeToProcessMedia: MediaSizes): string {
    const noNeedToAppendExtension = FileUtil.urlHasExtension(url) || url.includes('-blob');
    if (noNeedToAppendExtension) {
      return url;
    }

    const extension = this.webpEnabled ? 'webp' : 'png';

    return `${url}-${sizeToProcessMedia}.${extension}`;
  }
}
